// ─── Sanctum Google Maps Service ───────────────────────────────────────────────
// Ported from Yonder's src/services/maps.ts. Handles: Maps JS API loading, stop
// geocoding, route directions, place details. Styled for Sanctum's sacred &
// ancestral voice: deep desaturated forest green map, candlelight-gold route.
// Used by: JourneyScreen map component

export interface LatLng {
  lat: number
  lng: number
}

export interface MapsStop {
  name: string
  position: LatLng
  placeId?: string
  address?: string
  travelTimeFromPrev?: string   // e.g. "12 mins"
  distanceFromPrev?: string     // e.g. "4.2 km"
  imageUrl?: string             // stop photo thumbnail, for the InfoWindow popup
  timeLabel?: string            // e.g. "Day 1" — the time-of-day/day label, for the InfoWindow popup
}

export interface RouteResult {
  stops: MapsStop[]
  totalDuration: string
  totalDistance: string
}

// ─── Known stop coordinates (curated, avoids geocoding costs for fixed data) ──
// Covers all 12 Sanctum destinations and their stops. Walking-route destinations
// (Camino de Santiago, Kumano Kodo, Via Francigena) use approximate midpoint /
// waypoint coordinates for each named stage rather than a single city point.
// In Phase 2, these will come from the Places API dynamically.
const CURATED_COORDS: Record<string, LatLng> = {
  // ── Ireland (Root Seeker) ──
  'national archives your family name':      { lat: 53.3498, lng: -6.2603 }, // Dublin
  'the ancestral townland':                  { lat: 53.4129, lng: -8.2439 }, // rural Co. Galway
  'the local churchyard':                    { lat: 53.2707, lng: -9.0568 }, // Co. Galway parish
  'cliffs of moher the edge of the old world':{ lat: 52.9715, lng: -9.4309 },
  'skellig michael ancient monastic island': { lat: 51.7713, lng: -10.5379 },

  // ── Sicily (Root Seeker) ──
  'valley of the temples agrigento':         { lat: 37.2911, lng: 13.5865 },
  'the ancestral comune':                    { lat: 37.5079, lng: 14.0940 }, // Enna province
  'palermo palatine chapel':                 { lat: 38.1113, lng: 13.3524 },
  'mount etna the living landscape':         { lat: 37.7510, lng: 14.9934 },
  'the family meal return visit':            { lat: 37.6000, lng: 14.0154 }, // central Sicily village

  // ── Poland (Root Seeker) ──
  'krakow old town royal castle':            { lat: 50.0547, lng: 19.9356 },
  'krakw old town royal castle':             { lat: 50.0547, lng: 19.9356 }, // accented ó strips out
  'the ancestral village':                   { lat: 50.2500, lng: 20.5000 }, // rural Małopolska
  'auschwitzbirkenau memorial':               { lat: 50.0359, lng: 19.1783 },
  'd jewish heritage trail':                 { lat: 51.7592, lng: 19.4560 }, // Łódź — accented chars strip to this key
  'lodz jewish heritage trail':              { lat: 51.7592, lng: 19.4560 },
  'warsaw rising museum':                    { lat: 52.2231, lng: 20.9816 },

  // ── Jerusalem (The Believer) ──
  'western wall morning prayers':            { lat: 31.7767, lng: 35.2345 },
  'via dolorosa the way of sorrow':          { lat: 31.7794, lng: 35.2312 },
  'dome of the rock temple mount':           { lat: 31.7780, lng: 35.2354 },
  'armenian quarter the hidden city':        { lat: 31.7745, lng: 35.2280 },
  'yad vashem and the garden tomb':          { lat: 31.7743, lng: 35.1755 },

  // ── Varanasi (The Believer) ──
  'dawn boat on the ganges':                 { lat: 25.3109, lng: 83.0107 },
  'manikarnika ghat the burning ghat':       { lat: 25.3080, lng: 83.0140 },
  'vishwanath temple the golden temple':     { lat: 25.3109, lng: 83.0107 },
  'ganga aarti ceremony':                    { lat: 25.3140, lng: 83.0105 }, // Dashashwamedh Ghat
  'sarnath where the buddha first taught':   { lat: 25.3811, lng: 83.0230 },

  // ── Rome pilgrimage (The Believer) ──
  'st peters basilica first light':          { lat: 41.9022, lng: 12.4539 },
  'castel santangelo and the angel':         { lat: 41.9031, lng: 12.4663 },
  'papal audience wednesday':                { lat: 41.9029, lng: 12.4534 },
  'catacombs of san callisto':               { lat: 41.8531, lng: 12.5147 },
  'seven churches of rome pilgrimage':       { lat: 41.8871, lng: 12.5013 }, // St John Lateran

  // ── Camino de Santiago (Ancestral Pilgrim) — waypoint midpoints along the Camino Francés ──
  'stjeanpieddeport to roncesvalles':        { lat: 43.0125, lng: -1.3195 }, // midpoint of the Pyrenees crossing
  'burgos gothic cathedral of light':        { lat: 42.3439, lng: -3.6969 },
  'o cebreiro the galician crossing':        { lat: 42.7069, lng: -7.0499 },
  'monte do gozo the first sight':           { lat: 42.8886, lng: -8.5288 },
  'cathedral of santiago the pilgrim mass':  { lat: 42.8805, lng: -8.5444 },

  // ── Kumano Kodo (Ancestral Pilgrim) ──
  'nachi grand shrine and waterfall':        { lat: 33.6669, lng: 135.8871 },
  'dainichigoe pass deep forest':            { lat: 33.7333, lng: 135.7667 }, // midpoint pass between Nachisan/Hongu
  'hongu taisha grand shrine':               { lat: 33.8339, lng: 135.7731 },
  'yunomine onsen sacred hot spring':        { lat: 33.8167, lng: 135.7500 },
  'hayatama taisha and the sea':             { lat: 33.7247, lng: 135.9903 },

  // ── Via Francigena (Ancestral Pilgrim) — waypoint midpoints along the route ──
  'great st bernard pass the alps':          { lat: 45.8695, lng: 7.1704 },
  'santhi to vercelli the po plain':         { lat: 45.3833, lng: 8.4167 }, // midpoint Santhià–Vercelli (accented à strips to this key)
  'santhia to vercelli the po plain':        { lat: 45.3833, lng: 8.4167 },
  'siena the perfect medieval city':         { lat: 43.3188, lng: 11.3308 },
  'bolsena to viterbo via cassia':           { lat: 42.6167, lng: 12.0333 }, // midpoint Bolsena–Viterbo
  'rome st peters basilica':                 { lat: 41.9022, lng: 12.4539 },

  // ── Bhutan (Spiritual Wanderer) ──
  'paro valley arrival and acclimatisation': { lat: 27.4287, lng: 89.4164 },
  'tigers nest monastery paro taktsang':     { lat: 27.4914, lng: 89.3633 },
  'punakha dzong the winter palace':         { lat: 27.5916, lng: 89.8776 },
  'gangtey valley blacknecked cranes':       { lat: 27.5083, lng: 90.1583 },
  'thimphu memorial chorten':                { lat: 27.4694, lng: 89.6392 },

  // ── Scottish Highlands (Spiritual Wanderer) ──
  'iona the sacred island':                  { lat: 56.3300, lng: -6.3900 },
  'glen coe the ancient mountains':          { lat: 56.6836, lng: -5.1058 },
  'loch ness and the great glen':            { lat: 57.3229, lng: -4.4244 },
  'duncansby head the final corner':         { lat: 58.6539, lng: -3.0244 },
  'skye the cuillin and fairy pools':        { lat: 57.2445, lng: -6.2580 },

  // ── Mont Saint-Michel (Spiritual Wanderer) ──
  'abbey at first light before the world arrives': { lat: 48.6361, lng: -1.5115 },
  'tidal crossing on foot':                  { lat: 48.6280, lng: -1.5200 }, // bay sands approach
  'pontorson and the selune valley':         { lat: 48.5578, lng: -1.5117 },
  'normandy dday beaches':                   { lat: 49.3400, lng: -0.7300 }, // Omaha Beach area
  'bayeux cathedral and the tapestry':       { lat: 49.2764, lng: -0.7024 },

  // ── Destination-level fallback centres (used for map centring / DNA cards) ──
  'ireland':                { lat: 53.4129, lng: -8.2439 },
  'sicily':                 { lat: 37.5990, lng: 14.0154 },
  'poland':                 { lat: 50.0647, lng: 19.9450 },
  'jerusalem':              { lat: 31.7683, lng: 35.2137 },
  'varanasi':               { lat: 25.3176, lng: 82.9739 },
  'rome the eternal city':  { lat: 41.9028, lng: 12.4964 },
  'camino de santiago':     { lat: 42.8805, lng: -8.5444 },
  'kumano kodo':            { lat: 33.7500, lng: 135.8500 },
  'via francigena':         { lat: 43.7696, lng: 11.2558 },
  'bhutan':                 { lat: 27.5142, lng: 90.4336 },
  'scottish highlands':     { lat: 57.0000, lng: -4.9000 },
  'mont saintmichel':       { lat: 48.6361, lng: -1.5115 },
}

function findCoords(stopName: string): LatLng | null {
  const key = stopName
    .toLowerCase()
    .replace(/[🌿🕯🚶🌄🏠🙏📖☮💒🏘🌊🧘👥👨‍👩‍👧🤝🔍✅💭🍀🍋🦅🕌🪔✝⚜⛩🏔🏰◈]/gu, '')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

  // Exact match first
  if (CURATED_COORDS[key]) return CURATED_COORDS[key]

  // Try progressively shorter matches
  const shortKey = key.split(' ').slice(0, 4).join(' ')
  for (const [k, v] of Object.entries(CURATED_COORDS)) {
    if (key.includes(k) || k.includes(shortKey) || shortKey.includes(k)) return v
  }
  return null
}

// ─── Maps JS API Loader ────────────────────────────────────────────────────────

let mapsPromise: Promise<typeof google.maps> | null = null

export function loadGoogleMaps(): Promise<typeof google.maps> {
  if (mapsPromise) return mapsPromise

  mapsPromise = new Promise((resolve, reject) => {
    // Key resolution: build-time env first (local dev), then runtime config
    // served by the Pages worker (production — dashboard-managed, referrer-restricted).
    const resolveKey = async (): Promise<string> => {
      const buildKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
      if (buildKey && buildKey !== 'AIza_your_maps_key_here') return buildKey
      const res = await fetch('/api/config')
      if (!res.ok) throw new Error('config unavailable')
      const cfg = await res.json()
      if (!cfg.mapsKey) throw new Error('Google Maps API key not configured')
      return cfg.mapsKey as string
    }

    if (window.google?.maps) {
      resolve(window.google.maps)
      return
    }

    resolveKey().then((key) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.google.maps)
    script.onerror = () => reject(new Error('Google Maps script failed to load'))
    document.head.appendChild(script)
    }).catch(reject)
  })

  return mapsPromise
}

// ─── Initialise a map on a container element ───────────────────────────────────
// Sanctum styling: deep desaturated forest green / near-black, muted candlelight
// gold roads, no clutter — a near-monastic map, not a tourist one.

export async function initMap(
  container: HTMLElement,
  center: LatLng,
  zoom = 11
): Promise<google.maps.Map> {
  const maps = await loadGoogleMaps()
  return new maps.Map(container, {
    center,
    zoom,
    disableDefaultUI: true,
    zoomControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#0D2B1A' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#0A1A10' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#8FA394' }] },
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2D5A3D' }, { weight: 0.6 }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0A1F14' }] },
      { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#132F1E' }] },
      { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#152F1F' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1E3B27' }] },
      { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#C9973A' }] },
      { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2D5A3D' }] },
    ],
  })
}

// ─── Place numbered markers ────────────────────────────────────────────────────

// Teardrop pin path (classic map-pin silhouette), centred so the label sits
// in the pin's head — candlelight-gold fill, ivory stroke, matching the
// parent app's marker treatment instead of a plain numbered circle.
const TEARDROP_PIN_PATH = 'M 0,-24 C -8,-24 -14,-18 -14,-10 C -14,-1 -6,8 0,18 C 6,8 14,-1 14,-10 C 14,-18 8,-24 0,-24 Z'

// Builds the styled InfoWindow HTML for a stop: dark forest card, candlelight-gold
// accent border, the stop's photo thumbnail (if available), name and time/day label.
// Falls back to just name + label when no image has resolved.
function buildInfoWindowContent(stop: MapsStop, index: number): string {
  const imageBlock = stop.imageUrl
    ? `<div style="width:100%;height:96px;overflow:hidden;border-bottom:1px solid rgba(201,151,58,0.3);">
         <img src="${stop.imageUrl}" alt="${stop.name}" style="width:100%;height:100%;object-fit:cover;filter:saturate(0.8) brightness(0.85);" />
       </div>`
    : ''

  return `
    <div style="background:#0E2717;border:1px solid #C9973A;min-width:180px;max-width:220px;font-family:'EB Garamond',Georgia,serif;overflow:hidden;">
      ${imageBlock}
      <div style="padding:10px 12px;">
        ${stop.timeLabel ? `<div style="font-family:'Cinzel',Georgia,serif;font-size:9px;letter-spacing:0.1em;color:#E8C47A;text-transform:uppercase;margin-bottom:4px;">${stop.timeLabel}</div>` : ''}
        <div style="font-family:'Cinzel',Georgia,serif;font-size:12px;color:#F8F5EF;letter-spacing:0.03em;line-height:1.4;">${index + 1}. ${stop.name}</div>
      </div>
    </div>
  `
}

export async function placeMarkers(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<google.maps.Marker[]> {
  const maps = await loadGoogleMaps()
  let openInfoWindow: google.maps.InfoWindow | null = null

  return stops.map((stop, i) => {
    const marker = new maps.Marker({
      position: stop.position,
      map,
      title: stop.name,
      label: {
        text: String(i + 1),
        color: '#081C10',
        fontWeight: 'bold',
        fontSize: '12px',
        fontFamily: 'Cinzel, Georgia, serif',
      },
      icon: {
        path: TEARDROP_PIN_PATH,
        fillColor: '#C9973A',
        fillOpacity: 1,
        strokeColor: '#F8F5EF',
        strokeWeight: 1.5,
        scale: 1.15,
        anchor: new maps.Point(0, 18),
        labelOrigin: new maps.Point(0, -10),
      },
    })

    const infoWindow = new maps.InfoWindow({
      content: buildInfoWindowContent(stop, i),
      ariaLabel: stop.name,
    })

    marker.addListener('click', () => {
      if (openInfoWindow) openInfoWindow.close()
      infoWindow.open(map, marker)
      openInfoWindow = infoWindow
    })

    return marker
  })
}

// ─── Draw route polyline between stops ────────────────────────────────────────
// Candlelight-gold dashed path, quieter than a solid line — fitting Sanctum's
// slow, reverent pacing.

export async function drawRoute(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const path = stops.map((s) => s.position)
  new maps.Polyline({
    path,
    geodesic: true,
    strokeColor: '#C9973A',
    strokeOpacity: 0.65,
    strokeWeight: 2.5,
    icons: [{
      icon: { path: maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 2.6, strokeColor: '#E8C47A' },
      offset: '50%',
      repeat: '140px',
    }],
    map,
  })
}

// ─── Fit map bounds to all stops ───────────────────────────────────────────────

export async function fitBounds(
  map: google.maps.Map,
  stops: MapsStop[]
): Promise<void> {
  const maps = await loadGoogleMaps()
  const bounds = new maps.LatLngBounds()
  stops.forEach((s) => bounds.extend(s.position))
  map.fitBounds(bounds, { top: 40, right: 20, bottom: 40, left: 20 })
}

// ─── Resolve stop coordinates (curated first, geocode fallback) ───────────────

export async function resolveStopCoordinates(
  stopNames: string[],
  context?: string,
  extras?: { imageUrl?: string; timeLabel?: string }[]
): Promise<MapsStop[]> {
  const results: MapsStop[] = []

  for (let idx = 0; idx < stopNames.length; idx++) {
    const name = stopNames[idx]
    const extra = extras?.[idx]
    const curated = findCoords(name)
    if (curated) {
      results.push({ name, position: curated, imageUrl: extra?.imageUrl, timeLabel: extra?.timeLabel })
      continue
    }

    // Geocode fallback via Maps Geocoding API
    try {
      const maps = await loadGoogleMaps()
      const geocoder = new maps.Geocoder()
      const cleanName = name.replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').split(/[\u2014\u2013-]/)[0].trim()
      const response = await geocoder.geocode({ address: context ? `${cleanName}, ${context}` : cleanName })
      if (response.results.length > 0) {
        const loc = response.results[0].geometry.location
        results.push({
          name,
          position: { lat: loc.lat(), lng: loc.lng() },
          address: response.results[0].formatted_address,
          placeId: response.results[0].place_id,
          imageUrl: extra?.imageUrl,
          timeLabel: extra?.timeLabel,
        })
      }
    } catch {
      console.warn(`Could not geocode stop: ${name}`)
    }
  }

  return results
}

// ─── Get travel times between consecutive stops via Directions API ────────────

export async function getTravelTimes(stops: MapsStop[]): Promise<MapsStop[]> {
  if (stops.length < 2) return stops

  try {
    const maps = await loadGoogleMaps()
    const service = new maps.DirectionsService()
    const waypoints = stops.slice(1, -1).map((s) => ({ location: s.position, stopover: true }))

    const result = await service.route({
      origin: stops[0].position,
      destination: stops[stops.length - 1].position,
      waypoints,
      travelMode: maps.TravelMode.DRIVING,
    })

    const legs = result.routes[0]?.legs ?? []
    return stops.map((stop, i) => ({
      ...stop,
      travelTimeFromPrev: i > 0 ? legs[i - 1]?.duration?.text : undefined,
      distanceFromPrev: i > 0 ? legs[i - 1]?.distance?.text : undefined,
    }))
  } catch {
    return stops
  }
}
